import type { FC, HTMLAttributes, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

interface CardsSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, `title`> {
  title: string | null
  isEmpty?: boolean
  isLoading?: boolean
}

const CardsSection: FC<PropsWithChildren<CardsSectionProps>> = ({
  title,
  isEmpty,
  isLoading = false,
  children,
  ...rest
}) => {
  return (
    <StyledSectionBase isEmpty={isEmpty} variant="noCopy" {...rest}>
      <Section.Contents>
        {title && (
          <StyledCopyTextBase isEmpty={isEmpty}>
            <CopyText.Heading tag="h2" variant="h4">
              {title}
            </CopyText.Heading>
          </StyledCopyTextBase>
        )}
        {children}
      </Section.Contents>
      <LoadingOverlay aria-busy={isLoading} isLoading={isLoading} />
    </StyledSectionBase>
  )
}

const StyledCopyTextBase = styled(CopyTextBase)<Omit<CardsSectionProps, `title`>>`
  margin-bottom: ${({ isEmpty }) => (isEmpty ? `16px` : `40px`)};
`

const StyledSectionBase = styled(SectionBase)<Omit<CardsSectionProps, `title`>>`
  position: relative;
  ${({ isEmpty }) =>
    isEmpty &&
    css`
      ${Section.Wrapper} {
        padding-top: 40px;
        padding-bottom: 40px;
      }
    `}
`

interface LoadingOverlayProps {
  isLoading: boolean
}

const LoadingOverlay = styled.div<LoadingOverlayProps>`
  opacity: 0;
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  transition: opacity ease-in-out 0.3s;
  z-index: 1;
  pointer-events: none;
  ${({ isLoading }) =>
    isLoading &&
    css`
      opacity: 0.5;
    `}
`

export default CardsSection
